/**
 * FlowAI Workspace-Liste — alle FlowAI-Workspaces mit Status-Pillen.
 *
 * @page /flowai/workspaces
 * @since v2.484.0
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Card, Button } from '../../components/ui';
import { IconCases } from '../../components/Icons';
import FlowAIBreadcrumb from '../../components/flowai/FlowAIBreadcrumb';

const API = import.meta.env.VITE_API_URL || '';

export default function FlowAIWorkspaceList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');

  async function load() {
    setLoading(true);
    const r = await fetch(`${API}/api/flowai/workspaces`, { credentials: 'include' });
    const data = await r.json().catch(() => ({}));
    setRows(Array.isArray(data) ? data : (data?.workspaces ?? []));
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function create() {
    if (!newName.trim()) return;
    const r = await fetch(`${API}/api/flowai/workspaces`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName.trim() }),
    });
    const out = await r.json().catch(() => ({}));
    if (r.ok) {
      setShowNew(false);
      setNewName('');
      load();
    } else {
      alert('Anlegen fehlgeschlagen: ' + (out.error || r.status));
    }
  }

  return (
    <div className="p-6">
      <FlowAIBreadcrumb trail={[{ label: 'Workspaces' }]} />
      <PageHeader
        title="FlowAI Workspaces"
        subtitle="Logische Trennung pro Team — Customer Success, Sales, etc."
        actions={<Button onClick={() => setShowNew(true)}>+ Neuer Workspace</Button>}
      />

      {showNew && (
        <Card className="mb-4">
          <div className="flex gap-2 items-center">
            <input
              autoFocus
              placeholder="Workspace-Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="flex-1 px-3 py-2 border border-[var(--border-subtle,#E5E7EB)] rounded-lg text-sm"
            />
            <Button onClick={create}>Anlegen</Button>
            <Button variant="ghost" onClick={() => setShowNew(false)}>Abbrechen</Button>
          </div>
        </Card>
      )}

      <Card padding="sm">
        {loading ? (
          <div className="p-4 text-sm">Lade…</div>
        ) : (
          <div className="divide-y divide-[var(--border-subtle,#E5E7EB)]">
            {rows.map((ws) => (
              <button
                key={ws.id}
                onClick={() => navigate(`/flowai/workspaces/${ws.id}`)}
                className="w-full text-left p-3 hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors flex items-center gap-3"
              >
                <div className="flex-1">
                  <div className="text-sm font-medium">{ws.name}</div>
                  <div className="text-[11px] text-[var(--text-tertiary,#6B7280)]">{ws.description || 'Keine Beschreibung'}</div>
                </div>
                <div className="text-[11px] text-[var(--text-tertiary,#6B7280)]">{ws.playbook_count || 0} Playbooks · {ws.skill_count || 0} Skills</div>
                <span className="text-[var(--text-tertiary,#6B7280)] text-lg">›</span>
              </button>
            ))}
            {rows.length === 0 && (
              <div className="p-12 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[var(--bg-inset,#F8F9FC)] mb-3">
                  <IconCases className="w-6 h-6 text-[var(--text-tertiary,#6B7280)]" />
                </div>
                <div className="text-sm font-medium mb-1">Noch keine Workspaces</div>
                <div className="text-[12px] text-[var(--text-tertiary,#6B7280)] mb-3">Lege einen Workspace pro Team an (Customer Success, Sales, ...).</div>
                <Button onClick={() => setShowNew(true)}>+ Ersten Workspace anlegen</Button>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
